// ────────────────────────────────────────────────────────────
// 이벤트 허브 — HUB_EVENTS 목록
// 작성 가이드: 아래 JSDoc (@example) 참고
// ────────────────────────────────────────────────────────────

import {
  getScheduleForHubPopup,
  getScheduleSortEnd,
  getScheduleSortStart,
  parseDateTime,
  resolveSchedule,
} from './_lib/schedule';
import { SUB, type HubEvent } from './_lib/types';

export { SUB, type HubEvent };

export {
  getEventStatus,
  getActiveEvents,
  getEventKey,
} from './_lib/eventStatus';

export type {
  EventMainStatus,
  EventSubStatus,
  EventStatus,
} from './_lib/eventStatus';

export type HubEventSchedulePopup = {
  startDate?: string;
  endDate?: string;
};

/** HUB_EVENTS href → 이탈팝업 등에서 쓸 게시기간. period 우선, 없으면 recruitment */
export function getHubEventSchedule(href: string): HubEventSchedulePopup | undefined {
  const event = HUB_EVENTS.find((e) => e.href === href);
  if (!event) return undefined;

  const range = getScheduleForHubPopup(resolveSchedule(event));
  if (!range) return undefined;

  return { startDate: range.start, endDate: range.end };
}

function compareByDate(a: HubEvent, b: HubEvent): number {
  const aStart = getScheduleSortStart(resolveSchedule(a));
  const bStart = getScheduleSortStart(resolveSchedule(b));

  const startDiff =
    parseDateTime(bStart).getTime() - parseDateTime(aStart).getTime();
  if (startDiff !== 0) return startDiff;

  const aEnd = getScheduleSortEnd(resolveSchedule(a));
  const bEnd = getScheduleSortEnd(resolveSchedule(b));

  if (!aEnd && !bEnd) return 0;
  if (!aEnd) return 1;
  if (!bEnd) return -1;

  return (
    parseDateTime(aEnd, true).getTime() - parseDateTime(bEnd, true).getTime()
  );
}

function sortByDate(events: HubEvent[]): HubEvent[] {
  return [...events].sort(compareByDate);
}

function sortEvents(events: HubEvent[]): HubEvent[] {
  const pinned = events.filter((event) => event.order != null);
  const unpinned = events.filter((event) => event.order == null);

  pinned.sort((a, b) => {
    const orderDiff = a.order! - b.order!;
    if (orderDiff !== 0) return orderDiff;
    return compareByDate(a, b);
  });

  return [...pinned, ...sortByDate(unpinned)];
}

/**
 * HUB_EVENTS 작성 가이드 — 타입·상세 규칙은 `_lib/types.ts` 참고
 *
 * ## 필수
 * - id, category, href, imageKey, schedule
 * - slideTitle / cardTitle 중 하나 이상 (이미지는 imageKey로 자동 연결)
 *
 * ## schedule
 * - `start`만 → 상시 (always)
 * - `start` + `end` → 기간 한정 (live / soon)
 * - `recruitment` → 모집중 뱃지 (겹치면 live + recruiting)
 * - `end` 없는 상시는 recruitment 뱃지와 조합하지 않음
 *
 * ## schedule.cycle (선택 — start/end 윈도우 안 주기 반복, pending과 무관)
 *
 * ## schedule.pending (선택 — 1회성 base 대체, 활성 시 cycle보다 우선)
 *
 * ## 선택 (미지정 시 기본값)
 * - `soonDays` → 3 (0이면 마감임박 비표시)
 * - `slideColor` → `'white'` (`'black'`은 어두운 배경용)
 * - `target`, `order`, `memo`, `schedule.pending` → 생략
 * - /event 내부는 기존 id 기준으로 /event/{id}, /event/{id}-..., /event/{id}/...를 같은 일정으로 종료
 * - 종료일 변경 시 page에 조건을 추가하지 않음
 *
 * @example
 * {
 *   id: 'event-id',
 *   category: SUB.SHARED_OFFICE,
 *   href: '/event/example',
 *   imageKey: 'example',  
 *   ㄴ banner_example.jpg, banner_example_mo.jpg, card_example.jpg
 *
 *   schedule: {
 *     start: '2026-01-01',
 *     end: '2026-12-31',
 *     cycle: 7,
 *      
 *     // [모집중]
 *     recruitment: { start: '2026-01-01', end: '2026-01-31' }, 
 *
 *     // [1회성 예약]
 *     pending: { start: '2026-02-01', end: '2026-02-28' }, 
 *
 *     // [n일 주기]
 *     start: '2026-01-01',
 *     end: '2026-12-31',
 *     cycle: 7,
 *
 *     // [매월]
 *     start: '2026-08-01',
 *     cycle: 'month',
 *   },
 *
 *   target: '_blank',   // 선택: 외부 링크
 *   order: 1,           // 선택: 수동 노출 순번 (작을수록 앞)
 *   memo: '부가 안내',   // 선택: 슬라이드·카드 하단
 *   soonDays: 3,        // 선택: 마감임박 기준일 (기본 3, 0이면 비표시)
 *                             
 *   slideTitle: <>배너 문구</>, 
 *   slideColor: 'black',        // 선택 : 지정 안하면 기본 'white'
 *   cardTitle: '카드 문구 (GA btnname)',
 * }
 */

export const HUB_EVENTS: HubEvent[] = sortEvents([
  {
    id: 'ff-rentfree',
    category: SUB.SHARED_OFFICE,
    href: '/event/ff-rentfree-contact',
    imageKey: 'rentfree',
    schedule: {
      start: '2026-11-02',
      end: '2026-11-30',
    },
    memo: '선착순 30팀 한정',
    slideTitle: <>2027년 공유오피스<br />6개월 무료 사무실</>,
    cardTitle: '선착순 마감 임박! 가격 인상 전 마지막 최저가',
    order : 1,
  },
  {
    id: 'ff-freetrial',
    category: SUB.SHARED_OFFICE,
    href: '/event/ff-freetrial',
    imageKey: 'freetrial',
    schedule: {
      start: '2026-10-26',
      end: '2026-11-08',
    },
    slideTitle: <>공유오피스<br />신규 고객 무료체험</>,
    cardTitle: '최대 2주 신규 고객 무료체험 모집!',
  },
  {
    id: 'ff-calculator-contact',
    category: SUB.SHARED_OFFICE,
    href: '/event/ff-calculator-contact',
    imageKey: 'calculator',
    schedule: {
      start: '2026-11-01', 
    },
    slideTitle: <>사무실 비용 <br />거품 계산기</>,
    cardTitle: '사무실, 제값 내고 쓰고 있나요?',
  },
  {
    id: 'ff-today',
    category: SUB.SHARED_OFFICE,
    href: '/event/ff-today-contact',
    imageKey: 'today',
    schedule: {
      start: '2026-10-06',
      end: '2026-11-02',
    },
    slideTitle: <>2026년 최저가<br />오늘의 특가</>,
    cardTitle: '2026 최저가 사무실 매물이 매일 쏟아진다!',
  },
  // {
  //   id: 'ff-open-promotion',
  //   category: SUB.SHARED_OFFICE,
  //   href: '/event/ff-open-promotion-contact',
  //   imageKey: 'openff', 
  //   soonDays: 0,
  //   schedule: {
  //     start: '2026-09-01',
  //     end: '2026-10-02',
  //   },
  //   slideTitle: <>뉴패파 오픈 기념!<br />공유오피스 무료</>,
  //   cardTitle: '뉴패파 오픈 기념 공유오피스 6개월 무료!',
  //   order: 1,
  // },
  {
    id: 'fs-lounge-open',
    category: SUB.LOUNGE_MEMBERSHIP,
    href: '/event/fs-lounge-open',
    imageKey: 'opensp_2611',
    schedule: {
      start: '2026-09-01',
      cycle: 'month',
    },
    slideTitle: <>1인 멤버십 최저가<br />40% 할인 쿠폰</>,
    cardTitle: '1인 라운지 멤버십 40% 할인 쿠폰',
  },
  {
    id: 'fs-virtual-office',
    category: SUB.LOUNGE_MEMBERSHIP,
    href: '/event/fs-virtual-office',
    imageKey: 'virtual_office',
    schedule: {
      start: '2026-09-30',
      end: '2026-10-31',
      pending: { start: '2026-11-01', end: '2026-11-30' },
    },
    slideTitle: <>비상주 찾으시죠? <br />1인 기업 패키지</>,
    cardTitle: '비상주 됩니다! 1인 기업 패키지',
  },
  {
    id: 'interior-webinar',
    category: SUB.OFFICE_INTERIOR,
    href: 'https://docurise.io/marketing/d/2611webinar?src=newff_eventhub',
    imageKey: 'interior_webinar_261020',
    schedule: {
      start: '2026-10-16',
      end: '2026-11-05 12:00:00',
      recruitment: { 
        start: '2026-10-16',
        end: '2026-11-05 12:00:00',
      },
    },
    memo: '11월 5일 14시 @ZOOM',
    slideTitle: <>사무실 이전, <br /> 놓치기 쉬운 비용 7</>,
    slideColor: 'white',
    cardTitle: '사무실 이전, 놓치기 쉬운 비용 7',
    target: '_blank',
  },
  {
    id: 'interior-installment',
    category: SUB.OFFICE_INTERIOR,
    href: '/blog/interior-installment',
    imageKey: 'interior_support',
    schedule: {
      start: '2026-04-23',
    },
    slideTitle: <>사무실 인테리어<br />무이자 할부 지원</>,
    slideColor: 'black',
    cardTitle: '인테리어 비용 무이자 할부',
  },
  {
    id: 'ff-new-all',
    category: SUB.SHARED_OFFICE,
    href: '/event/ff-new-all',
    imageKey: 'newff',
    soonDays: 0,
    schedule: {
      start: '2026-08-01',
      end: '2026-10-22',
      cycle: 'month',
    },
    slideTitle: <>공유오피스 신규 지점<br />최대 50% 할인</>,
    slideColor: 'black',
    cardTitle: '공유오피스 신규 지점 1인 20만 원부터',
  },
  {
    id: 'ff-lowest-price',
    category: SUB.SHARED_OFFICE,
    href: '/event/ff-lowest-price-contact',
    imageKey: 'lowestprice',
    soonDays: 0,
    schedule: {
      start: '2026-08-01',
      cycle: 'month',
    },
    slideTitle: <>공유오피스 인실별<br />최저가 바로 공개!</>,
    cardTitle: '공유오피스 인실별 최저가 상담 없이 확인',
  },
  {
    id: 'ff-partners',
    category: SUB.SHARED_OFFICE,
    href: '/ff-partners',
    imageKey: 'partners',
    soonDays: 0,
    schedule: {
      start: '2026-08-01',
      recruitment: { start: '2026-04-10' },
      cycle: 'month',
    },
    slideTitle: <>공유오피스 추천할 <br />인맥왕을 찾아요</>,
    slideColor: 'black',
    cardTitle: '친구가 공유오피스 투어하면 10만 원!',
  },
  {
    id: 'ff-open-notification',
    category: SUB.SHARED_OFFICE,
    href: '/event/ff-open-notification',
    imageKey: 'notification',
    soonDays: 0,
    schedule: {
      start: '2026-08-01',
      cycle: 'month',
    },
    cardTitle: '신규 지점 사전 예약 시 인테리어 지원!',
  },
  {
    id: 'ff-largeofficeextra',
    category: SUB.SHARED_OFFICE,
    href: '/event/ff-largeofficeextra',
    imageKey: 'largerentfree_260831',
    soonDays: 0,
    schedule: {
      start: '2026-08-01',
      cycle: 'month',
    },
    cardTitle: '20인 이상 기업 공유오피스 특별 할인',
  },
]);

